import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Small transient-toast hook.
 *
 * Seven components had been hand-rolling the same setState + setTimeout +
 * ref-backed clear-pending-timer dance. Consolidating here so behaviour is
 * consistent:
 *   - Calling `show` again while a toast is visible resets the timer
 *     (so a burst of errors doesn't hide the last one after the first's
 *     3-second window).
 *   - The pending timer is cleared on unmount (no setState-after-unmount
 *     warnings during route transitions).
 *
 * Usage:
 *   const { toast, show } = useToast()
 *   show('Saved')
 *   {toast && <div>...</div>}
 */
export interface UseToastOptions {
  /** How long (ms) a toast stays visible before auto-clearing. Default 3000. */
  durationMs?: number
}

export function useToast({ durationMs = 3000 }: UseToastOptions = {}) {
  const [toast, setToast] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clear = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setToast(null)
  }, [])

  // Per-call duration override wins over the construction-time default
  // — call sites that want a long-running error toast can pass a bigger
  // number without needing two separate hook instances.
  const show = useCallback((msg: string, overrideMs?: number) => {
    setToast(msg)
    if (timerRef.current != null) clearTimeout(timerRef.current)
    const ms = overrideMs ?? durationMs
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      setToast(null)
    }, ms)
  }, [durationMs])

  // Release the pending timer on unmount so it can't fire a stale setState
  // after the component is gone (React dev-mode warning, real footgun in
  // unit tests / fast route transitions).
  useEffect(() => {
    return () => { if (timerRef.current != null) clearTimeout(timerRef.current) }
  }, [])

  return { toast, show, clear }
}
